import { ReactNode, useRef } from 'react';

// Layouts
import DesktopHeader from './layouts/desktop_header';
import MobileHeader from './layouts/mobile_header';
import MobileFooter from './layouts/mobile_footer';
import MobileModal from './layouts/mobile_modal';

// Compound Components
import Title from './compounds/title';
import Navigation from './compounds/navigation';
import Link from './compounds/link';
import Actions from './compounds/actions';
import Primary from './compounds/primary';
import Secondary from './compounds/secondary';

// Hooks and utils
import useStickyDetection from './hooks/use_sticky_detection';
import useModalState from './hooks/use_modal_state';
import { extractChildren } from './utils/extract_children';

interface CTASubnavProps {
  children: ReactNode;
}

/**
 * Main CTASubnav compound component
 * Handles both desktop and mobile layouts with proper sticky behavior
 * All props passed to the compound components are then passed to the underlying components
 * If any new props need to be added, they should be added to the underlying components first
 * See: ./compounds/primary.tsx as an example
 *
 * @example
 * ```tsx react
 * <CTASubnav>
 *   <CTASubnav.Title>Product Name</CTASubnav.Title>
 *   <CTASubnav.Navigation>
 *     <CTASubnav.Link href="/overview">Overview</CTASubnav.Link>
 *     <CTASubnav.Link href="/specs">Specifications</CTASubnav.Link>
 *   </CTASubnav.Navigation>
 *   <CTASubnav.Actions>
 *     <CTASubnav.Secondary>Contact Sales</CTASubnav.Secondary>
 *     <CTASubnav.Primary>Add to Cart</CTASubnav.Primary>
 *   </CTASubnav.Actions>
 * </CTASubnav>
 * ```
 */
const CTASubnav = ({ children }: CTASubnavProps) => {
  // Create refs for both headers
  const desktopHeaderRef = useRef<HTMLDivElement>(null);
  const mobileHeaderRef = useRef<HTMLDivElement>(null);

  // Initialize sticky detection for both headers
  useStickyDetection(desktopHeaderRef);
  useStickyDetection(mobileHeaderRef);

  // Manage mobile modal state
  const {
    isOpen: isModalOpen,
    open: openModal,
    close: closeModal,
  } = useModalState();

  // Extract children by type
  const {
    titleChild,
    navLinks,
    primaryButton,
    secondaryButton,
    hasNavLinks,
    hasActions,
  } = extractChildren(children);

  // Render both layouts - CSS controls visibility based on breakpoint
  return (
    <>
      <DesktopHeader
        titleChild={titleChild}
        navLinks={navLinks}
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
        headerRef={desktopHeaderRef}
      />

      <MobileHeader
        titleChild={titleChild}
        hasNavLinks={hasNavLinks}
        openModal={openModal}
        headerRef={mobileHeaderRef}
      />

      {hasNavLinks && (
        <MobileModal
          isOpen={isModalOpen}
          onClose={closeModal}
          titleChild={titleChild}
          navLinks={navLinks}
        />
      )}

      {hasActions && (
        <MobileFooter
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
        />
      )}
    </>
  );
};

// Attach compound components as static properties
CTASubnav.Title = Title;
CTASubnav.Navigation = Navigation;
CTASubnav.Link = Link;
CTASubnav.Actions = Actions;
CTASubnav.Primary = Primary;
CTASubnav.Secondary = Secondary;

export default CTASubnav;
